import React from "react";
import moment from "moment";
import _ from "underscore";
import consola from "consola";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";

import ClientData from "../../../../../data/ClientData";
import UserData from "../../../../../data/UserData";
import ProtocolData from "../../../../../data/ProtocolData";
import { getMessageGroupWithUID } from "../../../../../data/MessageData";
import { WorkoutStatus } from "../../../../../data/GlobalData";
import { getRecoil } from "recoil-nexus";
import organizationIdState from "../../../../../atoms/organizationIdSelector";
import useClientSubscription from "../../../../../hooks/useClientSubscription";

export const useClientEffects = (uid, organizationId, client, setState) => {
  const workoutSubscription = React.useRef(undefined); // Add workout subscription ref
  const coachesBuffer = React.useRef([]);

  // Client subscription effect (refactored to use hook)
  const { client: subscribedClient } = useClientSubscription(
    uid,
    organizationId,
  );

  React.useEffect(() => {
    if (subscribedClient) {
      if (!_(subscribedClient.coaches).isEqual(coachesBuffer.current)) {
        coachesBuffer.current = subscribedClient.coaches;
        setState((prev) => ({ ...prev, coachesUpdated: new Date() }));
      }
      setState((prev) => ({ ...prev, client: subscribedClient }));
    }
  }, [subscribedClient, setState]);

  // Current workout subscription effect
  React.useEffect(() => {
    if (workoutSubscription.current) {
      workoutSubscription.current();
    }

    if (uid) {
      const organizationId = getRecoil(organizationIdState);

      // Subscribe to current incomplete workout
      const incompleteWorkoutRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(uid)
        .collection("reportExercise")
        .where("status", "==", WorkoutStatus.incomplete)
        .orderBy("createdOn", "desc")
        .limit(1);

      workoutSubscription.current = incompleteWorkoutRef.onSnapshot(
        (snapshot) => {
          consola.debug("++++++ SNAPSHOT -> useClientEffects: currentWorkout");

          if (snapshot.docs.length > 0) {
            setState((prev) => ({
              ...prev,
              currentWorkout: snapshot.docs[0].data(),
            }));
          } else {
            // No incomplete workout, check for today's workouts
            const today = new Date();
            const startOfDay = moment(today).startOf("day").toDate();
            const endOfDay = moment(today).endOf("day").toDate();

            const todayWorkoutRef = firebase
              .firestore()
              .collection("organizations")
              .doc(organizationId)
              .collection("clients")
              .doc(uid)
              .collection("reportExercise")
              .where("createdOn", ">=", startOfDay)
              .where("createdOn", "<=", endOfDay)
              .orderBy("createdOn", "desc")
              .limit(1);

            todayWorkoutRef
              .get()
              .then((todaySnapshot) => {
                consola.debug(
                  "++++++ READ -> useClientEffects: today's workout",
                );
                if (todaySnapshot.docs.length > 0) {
                  setState((prev) => ({
                    ...prev,
                    currentWorkout: todaySnapshot.docs[0].data(),
                  }));
                } else {
                  setState((prev) => ({
                    ...prev,
                    currentWorkout: null,
                  }));
                }
              })
              .catch((error) => {
                consola.error("Error fetching today's workout:", error);
              });
          }
        },
      );
    }

    return () => {
      if (workoutSubscription.current) {
        workoutSubscription.current();
      }
    };
  }, [uid, setState]);

  // Cleanup on beforeunload
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      consola.debug("****** UNLOAD ClientSummary - beforeunload");
      if (workoutSubscription.current) {
        workoutSubscription.current();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (workoutSubscription.current) {
        workoutSubscription.current();
      }
    };
  }, []);

  // Coaches loading effect
  React.useEffect(() => {
    const getCoaches = async (coachIds) => {
      if (!coachIds || !Array.isArray(coachIds) || coachIds.length === 0) {
        setState((prev) => ({ ...prev, coaches: [] }));
        return;
      }

      try {
        // First try to get coaches with Promise.all for better performance
        const promises = coachIds.map((coachId) => {
          return UserData.getUserData(coachId);
        });

        let coaches = await Promise.all(promises);

        // If any coach is null, fall back to sequential loading
        if (coaches.some((coach) => coach === null)) {
          console.log("Some coaches failed to load, trying sequential loading");
          coaches = [];
          for (const coachId of coachIds) {
            try {
              const coach = await UserData.getUserData(coachId);
              if (coach) coaches.push(coach);
            } catch (err) {
              console.error(`Failed to load coach ${coachId}:`, err);
            }
          }
        }

        const sorted = _.sortBy(
          coaches.filter((coach) => coach),
          "firstName",
        );
        setState((prev) => ({ ...prev, coaches: sorted }));
      } catch (error) {
        console.error("Error loading coaches:", error);
        setState((prev) => ({ ...prev, coaches: [] }));
      }
    };

    setState((prev) => {
      if (client?.coaches && Array.isArray(client.coaches)) {
        getCoaches(client.coaches);
        // Save to buffer for future reference
        coachesBuffer.current = client.coaches;
      } else if (coachesBuffer.current && coachesBuffer.current.length > 0) {
        getCoaches(coachesBuffer.current);
      }
      return prev;
    });
  }, [setState, client]);

  // Message group effect
  React.useEffect(() => {
    const getMessageData = async () => {
      if (!client?.uid) return;

      try {
        const group = await getMessageGroupWithUID([client.uid]);

        if (group.length >= 1) {
          setState((prev) => ({ ...prev, messageGroup: group[0] }));
        } else {
          setState((prev) => ({ ...prev, messageGroup: null }));
        }
      } catch (error) {
        consola.error("Error fetching message group:", error);
        setState((prev) => ({ ...prev, messageGroup: null }));
      }
    };

    if (client) {
      getMessageData();
    }
  }, [client, setState]);

  // Protocol loading: coach-assigned primary vs active plan in the client app
  React.useEffect(() => {
    let cancelled = false;

    const loadProtocols = async () => {
      const primaryId = client?.primaryProtocolId || null;
      const activeId = client?.protocolId || null;

      if (!primaryId && !activeId) {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            protocol: null,
            activeProtocol: null,
          }));
        }
        return;
      }

      // 1) Try coach primary anchor first (when present).
      // 2) If missing in Firestore (stale/wrong id), fall back to protocolId — that is the
      //    client’s active assignment and matches what coaches expect to see.
      let protocolDoc = null;
      if (primaryId) {
        try {
          protocolDoc = await ProtocolData.getProtocol(
            primaryId,
            organizationId,
          );
        } catch (error) {
          consola.error("Error loading primary protocol:", error);
        }
      }
      if (!protocolDoc && activeId) {
        try {
          protocolDoc = await ProtocolData.getProtocol(
            activeId,
            organizationId,
          );
        } catch (error) {
          consola.error("Error loading fallback protocol:", error);
        }
      }

      const resolvedAssignedId =
        (protocolDoc && protocolDoc.id) || primaryId || activeId;

      const needsActive =
        Boolean(activeId) &&
        Boolean(resolvedAssignedId) &&
        activeId !== resolvedAssignedId;

      let activeDoc = null;
      if (needsActive) {
        try {
          activeDoc = await ProtocolData.getProtocol(
            activeId,
            organizationId,
          );
        } catch (error) {
          consola.error("Error loading active protocol:", error);
        }
      }

      if (!cancelled) {
        setState((prev) => ({
          ...prev,
          protocol: protocolDoc,
          activeProtocol: needsActive ? activeDoc : null,
        }));
      }
    };

    if (client) {
      loadProtocols();
    }

    return () => {
      cancelled = true;
    };
  }, [
    client?.uid,
    client?.protocolId,
    client?.primaryProtocolId,
    organizationId,
    setState,
  ]);

  // Daily report subscription effect (updated to get all workouts)
  React.useEffect(() => {
    if (!client) return;

    const getDailyReport = async (selectedDay) => {
      try {
        // Changed from "complete" to "all" to get both complete and incomplete
        const report = await ClientData.getClientReport(
          client.uid,
          selectedDay,
          "all", // This will get both complete and incomplete workouts
        );
        setState((prev) => ({ ...prev, dailyReport: report }));
      } catch (error) {
        consola.error("Error fetching daily report:", error);
        setState((prev) => ({ ...prev, dailyReport: null }));
      }
    };

    // Get the current selected day from state
    let currentSelectedDay;
    setState((prev) => {
      currentSelectedDay = prev.selectedDay;
      return prev;
    });

    const subscription = ClientData.getHealthReportSubscription({
      uid: client.uid,
      date: moment(currentSelectedDay).toDate(),
      onSnapshot: () => {
        getDailyReport(currentSelectedDay);
      },
    });

    // Store subscription in state for potential cleanup elsewhere
    setState((prev) => ({
      ...prev,
      dailyReportSubscription: subscription,
    }));

    return () => {
      if (subscription) {
        subscription();
      }
      // Clear subscription from state
      setState((prev) => ({
        ...prev,
        dailyReportSubscription: null,
      }));
    };
  }, [client, setState]);
};
